import React from 'react';
import {
  AssignmentsOverlay,
} from '../../../../components/StrategyRoom/PlayerAssignments';
import VisionCones from '../../../../components/StrategyRoom/visioncones';

const StrategyCanvas = ({
  // Map data
  selectedMap,
  
  // Canvas state
  drawnLines,
  playersOnCanvas,
  utilitiesOnCanvas,
  textBoxes,
  visionCones,
  imagesOnCanvas,
  activeStep,
  selectedObject,
  activeTool,
  isCursorInside,
  cursorPos,
  penSettings,
  canvasDimensions,
  teamAssetsSide,
  showAssignmentsOverlay,
  assignPanelPos,
  assignmentRows,
  roleCounts,
  selectedConeType,
  
  // Refs
  pathRef,
  cursorRef,
  canvasSize,
  dragOffset,
  historyRef,
  
  // Actions
  onCanvasClick,
  onDrop,
  onPlayerClick,
  onPlayerMouseDown,
  onUtilityClick,
  onUtilityMouseDown,
  onTextBoxClick,
  onTextBoxMouseDown,
  onTextBoxInput,
  onImageClick,
  onImageMouseDown,
  onInitResize,
  onImageUpload,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
  onMouseEnter,
  onConesChange,
  onAssignDragStart,
  updateAssignment,
  generateUniqueId,
  handleCanvasClick,
}) => {
  return (
    <div
      className="map-canvas"
      onContextMenu={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      onClick={onCanvasClick}
      onDrop={onDrop}
      style={{
        position: 'relative',
        cursor: activeTool === 'eraser' || activeTool === 'pen' ? 'none' : 'default',
        touchAction: 'none'
      }}
    >
      <AssignmentsOverlay
        show={showAssignmentsOverlay}
        assignPanelPos={assignPanelPos}
        onDragStart={onAssignDragStart}
        assignmentRows={assignmentRows}
        roleCounts={roleCounts}
        updateAssignment={updateAssignment}
      />

      <div
        className="interaction-layer"
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1000
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1020,
          pointerEvents: 'none',
        }}
      >
        <VisionCones
          loadedCones={visionCones}
          coneType={selectedConeType}
          onConesChange={onConesChange}
        />
      </div>

      <svg
        className="map-overlay"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1000 600"
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1000,
          pointerEvents: 'auto',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        onClick={handleCanvasClick}
      >
        {/* MAP BACKGROUND IMAGE */}
        <image
          href={selectedMap.image}
          x="0"
          y="0"
          width="1000"
          height="600"
          preserveAspectRatio="xMidYMid meet"
        />

        {/* Drawings */}
        <g className="fade-lines" key={`step-${activeStep}`}>
          {drawnLines.map((line, index) => (
            <polyline
              key={index}
              points={line.points.map(p => `${p.x},${p.y}`).join(' ')}
              stroke={line.color}
              strokeWidth={line.thickness}
              strokeOpacity={line.opacity / 10}
              fill="none"
              strokeDasharray={line.type === 'dotted' ? '4 4' : 'none'}
              strokeLinecap="round"
              strokeLinejoin="round"
              markerEnd={line.type.includes('arrow') ? 'url(#arrow)' : undefined}
              className="fade-polyline"
            />
          ))}
        </g>

        {/* Live path */}
        <path ref={pathRef} fill="none" />

        {/* Players */}
        {playersOnCanvas.map((player) => {
          const px = (player.relX / 100) * 1000;
          const py = (player.relY / 100) * 600;
          const cardSide = player.side || 'ally';
          const cardFill = cardSide === 'enemy' ? '#d97706' : '#1e3a8a';
          return (
            <g key={player.id}>
              <rect
                x={px - 4}
                y={py - 4}
                width="34"
                height="34"
                rx="6"
                fill={cardFill}
                opacity="0.85"
                pointerEvents="none"
              />
              <image
                href={player.avatar}
                x={px}
                y={py}
                width="26"
                height="26"
                preserveAspectRatio="xMidYMid slice"
                style={{
                  cursor: 'grab',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                  outline:
                    selectedObject?.type === 'player' && selectedObject.id === player.id
                      ? '2px solid #7E57C2'
                      : 'none',
                  filter:
                    selectedObject?.type === 'player' && selectedObject.id === player.id
                      ? 'drop-shadow(0 0 6px #7E57C2)'
                      : 'none'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onPlayerClick(player);
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onPlayerMouseDown(e, player);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  onPlayerMouseDown(e, player);
                }}
              />
            </g>
          );
        })}

        {/* Utilities */}
        {utilitiesOnCanvas.map((util) => {
          const isMini = ['warning', 'anchor', 'flag', 'star'].includes(util.type);
          const utilSize = isMini ? 32 : 45;
          return (
            <image
              key={util.id}
              href={util.src || `/assets/${util.type}.png`}
              x={(util.relX / 100) * 1000}
              y={(util.relY / 100) * 600}
              width={utilSize}
              height={utilSize}
              preserveAspectRatio="xMidYMid slice"
              style={{
                pointerEvents: 'auto',
                cursor: 'grab',
                touchAction: 'none',
                outline: selectedObject?.type === 'utility' && selectedObject.id === util.id ? '2px solid #7E57C2' : 'none',
                filter: selectedObject?.type === 'utility' && selectedObject.id === util.id ? 'drop-shadow(0 0 6px #7E57C2)' : 'none'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onUtilityClick(util);
              }}
              onMouseDown={(e) => {
                onUtilityMouseDown(e, util);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                onUtilityMouseDown(e, util);
              }}
            />
          );
        })}

        {/* Arrow marker defs */}
        <defs>
          <marker
            id="arrow"
            markerWidth="10"
            markerHeight="10"
            refX="6"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,6 L9,3 z" fill={penSettings.color} />
          </marker>
        </defs>

        {/* Cursor indicators */}
        {activeTool === 'pen' && isCursorInside && (
          <image
            ref={cursorRef}
            href="/assets/pencil-icon.png"
            className="pen-cursor"
            x={cursorPos.x}
            y={cursorPos.y}
            width="24"
            height="24"
            pointerEvents="none"
          />
        )}

        {activeTool === 'eraser' && isCursorInside && (
          <image
            ref={cursorRef}
            href="/assets/eraser-icon_red.png"
            className="eraser-cursor"
            x={cursorPos.x}
            y={cursorPos.y}
            width="32"
            height="32"
            pointerEvents="none"
          />
        )}
      </svg>

      {/* Text boxes */}
      {textBoxes.map((tb) => (
        <div
          key={tb.id}
          className="resizable-textbox-wrapper"
          style={{
            position: 'absolute',
            top: `${(tb.relY / 100) * canvasDimensions.height}px`,
            left: `${(tb.relX / 100) * canvasDimensions.width}px`,
            width: tb.width,
            height: tb.height,
            minWidth: '60px',
            minHeight: '40px',
            zIndex: 1000
          }}
          onClick={(e) => {
            e.stopPropagation();
            onTextBoxClick(tb);
          }}
        >
          <div
            className="resize-handle left"
            onMouseDown={(e) => onInitResize(e, 'left', tb.id)}
            onTouchStart={(e) => {
              e.stopPropagation();
              onInitResize(e, 'left', tb.id);
            }}
          />

          <textarea
            className="canvas-textbox"
            placeholder="Text here..."
            value={tb.text}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = `${e.target.scrollHeight}px`;
              onTextBoxInput(tb.id, e.target.value, e.target.scrollHeight);
            }}
            style={{
              width: '100%',
              height: '100%',
              resize: 'none',
              overflow: 'hidden',
              backgroundColor: '#1f1f1f',
              color: 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: 'inherit',
              lineHeight: '1.4',
              touchAction: 'none'
            }}
            onMouseDown={(e) => {
              if (e.target.classList.contains('resize-handle')) return;
              onTextBoxMouseDown(e, tb);
            }}
            onTouchStart={(e) => {
              if (e.target.classList.contains('resize-handle')) return;
              onTextBoxMouseDown(e, tb);
            }}
          />

          <div
            className="resize-handle right"
            onMouseDown={(e) => onInitResize(e, 'right', tb.id)}
            onTouchStart={(e) => {
              e.stopPropagation();
              onInitResize(e, 'right', tb.id);
            }}
          />

          <div
            className="resize-handle bottom"
            onMouseDown={(e) => onInitResize(e, 'bottom', tb.id)}
            onTouchStart={(e) => {
              e.stopPropagation();
              onInitResize(e, 'bottom', tb.id);
            }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '8px',
              cursor: 'ns-resize',
              zIndex: 700
            }}
          />
        </div>
      ))}

      {/* Images on canvas */}
      {imagesOnCanvas && imagesOnCanvas.map((img) => {
        const left = img.relX !== undefined
          ? `${(img.relX / 100) * canvasDimensions.width}px`
          : `${img.x || 0}px`;
        const top = img.relY !== undefined
          ? `${(img.relY / 100) * canvasDimensions.height}px`
          : `${img.y || 0}px`;

        const isSelected = selectedObject?.type === 'image' && selectedObject.id === img.id;

        return (
          <img
            key={img.id}
            src={img.src}
            alt="Canvas asset"
            style={{
              position: 'absolute',
              left,
              top,
              width: `${img.width || 150}px`,
              height: `${img.height || 150}px`,
              zIndex: 900,
              pointerEvents: 'auto',
              touchAction: 'none',
              cursor: 'move',
              outline: isSelected ? '2px solid #7E57C2' : 'none',
              filter: isSelected ? 'drop-shadow(0 0 6px #7E57C2)' : 'none',
            }}
            draggable={false}
            onClick={(e) => {
              e.stopPropagation();
              onImageClick(img);
            }}
            onMouseDown={(e) => {
              onImageMouseDown(e, img);
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              onImageMouseDown(e, img);
            }}
          />
        );
      })}

      {/* Image upload input */}
      <input
        type="file"
        accept="image/*"
        id="imageUploadInput"
        style={{ display: 'none' }}
        onChange={onImageUpload}
      />
    </div>
  );
};

export default StrategyCanvas;

